import { useState } from "react";

interface Question {
  id: number;
  text: string;
  type: "multiple-choice" | "text";
  options?: string[];
  correctAnswer?: string;
  explanation?: string;
}

interface QuizModalProps {
  onComplete: () => void;
  onCancel: () => void;
}

const questions: Question[] = [
  {
    id: 1,
    text: "Do you remember that magical evening in Australia, when we watched the golden sun slowly drift into the horizon, and the beach sparkled like it was sprinkled with fairy dust? What was the name of that dreamy place where time seemed to pause just for us?",
    type: "multiple-choice",
    options: ["Bondi Beach", "St Kilda Beach", "Manly Beach", "Brighton Beach"],
    correctAnswer: "Brighton Beach",
  },
  {
    id: 2,
    text: "If our relationship were a movie, what would its title be?",
    type: "text",
    correctAnswer: undefined,
  },
  {
    id: 3,
    text: "Close your eyes and think about us. What's one little thing I do that always makes you think, 'Yep... that's the girl I fell in love with'?",
    type: "text",
    correctAnswer: undefined,
  },
  {
    id: 4,
    text: "Imagine we're old and telling our grandchildren about us. How would you describe our love story in one sentence?",
    type: "text",
    correctAnswer: undefined,
  },

  {
    id: 5,
    text: "A very important life decision... Choose wisely. 👀",
    type: "multiple-choice",
    options: ["🦫 The legendary Capybara", "💕 Esther, the love of your life"],
    correctAnswer: "💕 Esther, the love of your life",
    explanation:
      "Congratulations! You have successfully chosen the correct answer and protected your relationship. 😌❤️",
  },
];

export default function QuizModal({ onComplete, onCancel }: QuizModalProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState<string>("");
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  const checkAnswer = () => {
    if (!userAnswer.trim()) return;

    let correct = false;
    if (!currentQuestion.correctAnswer) {
      correct = true;
    } else {
      correct =
        currentQuestion.type === "multiple-choice"
          ? userAnswer === currentQuestion.correctAnswer
          : userAnswer
              .toLowerCase()
              .includes(
                (currentQuestion.correctAnswer as string).toLowerCase(),
              );
    }

    setIsCorrect(correct);
    if (correct) {
      setScore(score + 1);
    }
    setAnswered(true);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setUserAnswer("");
      setAnswered(false);
      setIsCorrect(false);
    } else {
      onComplete();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !answered) {
      checkAnswer();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 400,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          padding: "40px",
          maxWidth: "600px",
          width: "90%",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}
            >
              🎮 Quiz Challenge
            </div>
            <div style={{ fontSize: "14px", color: "#666" }}>
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>
          </div>
          <div
            style={{
              width: "100%",
              height: "6px",
              backgroundColor: "#e0e0e0",
              borderRadius: "3px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                backgroundColor: "#4CAF50",
                width: `${progress}%`,
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Question */}
        <div style={{ marginBottom: "30px" }}>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#333",
              marginBottom: "20px",
            }}
          >
            {currentQuestion.text}
          </div>

          {/* Multiple Choice */}
          {currentQuestion.type === "multiple-choice" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {currentQuestion.options?.map((option) => (
                <button
                  key={option}
                  onClick={() => !answered && setUserAnswer(option)}
                  disabled={answered}
                  style={{
                    padding: "14px 16px",
                    border: "2px solid #ddd",
                    borderRadius: "8px",
                    backgroundColor:
                      userAnswer === option
                        ? answered
                          ? isCorrect
                            ? "#4CAF50"
                            : "#f44336"
                          : "#E3F2FD"
                        : answered && option === currentQuestion.correctAnswer
                          ? "#4CAF50"
                          : "#fff",
                    color:
                      userAnswer === option && answered
                        ? "#fff"
                        : answered && option === currentQuestion.correctAnswer
                          ? "#fff"
                          : "#333",
                    cursor: answered ? "not-allowed" : "pointer",
                    fontSize: "15px",
                    fontWeight: "500",
                    textAlign: "left",
                    transition: "all 0.2s",
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Text Input */}
          {currentQuestion.type === "text" && (
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => !answered && setUserAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your answer here..."
              disabled={answered}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "15px",
                border: answered
                  ? isCorrect
                    ? "2px solid #4CAF50"
                    : "2px solid #f44336"
                  : "2px solid #ddd",
                borderRadius: "8px",
                boxSizing: "border-box",
                cursor: answered ? "not-allowed" : "text",
              }}
            />
          )}
        </div>

        {/* Feedback */}
        {answered && (
          <div
            style={{
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
              backgroundColor: isCorrect ? "#E8F5E9" : "#FFEBEE",
              color: isCorrect ? "#2E7D32" : "#C62828",
              fontSize: "14px",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              {isCorrect ? "✓ Correct!" : "✗ Incorrect"}
            </div>
            <div>{isCorrect ? currentQuestion.explanation : null}</div>
          </div>
        )}

        {/* Buttons */}
        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: "12px 24px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
              color: "#333",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#e0e0e0")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#f5f5f5")
            }
          >
            Cancel
          </button>

          {!answered ? (
            <button
              onClick={checkAnswer}
              disabled={!userAnswer.trim()}
              style={{
                padding: "12px 24px",
                backgroundColor: userAnswer.trim() ? "#4CAF50" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: userAnswer.trim() ? "pointer" : "not-allowed",
                fontSize: "15px",
                fontWeight: "600",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                if (userAnswer.trim()) {
                  e.currentTarget.style.backgroundColor = "#45a049";
                }
              }}
              onMouseOut={(e) => {
                if (userAnswer.trim()) {
                  e.currentTarget.style.backgroundColor = "#4CAF50";
                }
              }}
            >
              Submit
            </button>
          ) : (
            <button
              onClick={handleNext}
              style={{
                padding: "12px 24px",
                backgroundColor: "#2196F3",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "15px",
                fontWeight: "600",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#1976D2")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#2196F3")
              }
            >
              {currentQuestionIndex === questions.length - 1
                ? "Complete"
                : "Next"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
